import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Plus, Edit2, Trash2, Eye, EyeOff } from "lucide-react";

const CATEGORIES = [
  { id: "", label: "Toutes" },
  { id: "sneakers", label: "Sneakers" },
  { id: "wigs", label: "Suzel Luxury Hair" },
  { id: "couture", label: "Habillement" },
  { id: "epicerie", label: "Travel & Market" },
  { id: "patisserie", label: "Suzel Bakery" },
  { id: "printing", label: "Printing & IT" },
];

const AdminProducts = () => {
  const { authClient } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "";
  const [confirmingDelete, setConfirmingDelete] = useState(null);

  const load = () => {
    setLoading(true);
    const params = category ? `?category=${category}` : "";
    authClient()
      .get(`/api/admin/products${params}`)
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, [category, authClient]);

  const onCategoryChange = (cat) => {
    if (cat) setSearchParams({ category: cat });
    else setSearchParams({});
  };

  const onDelete = async (id) => {
    await authClient().delete(`/api/admin/products/${id}`);
    setConfirmingDelete(null);
    load();
  };

  const togglePublish = async (p) => {
    await authClient().put(`/api/admin/products/${p.id}`, { ...p, is_published: !p.is_published });
    load();
  };

  return (
    <div data-testid="admin-products-page" className="max-w-7xl">
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="overline mb-2">Catalogue</p>
          <h1 className="font-display text-4xl text-white">Produits</h1>
        </div>
        <Link to="/admin/products/new" data-testid="new-product-button" className="btn-gold">
          <Plus size={14} /> Nouveau produit
        </Link>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-8">
        {CATEGORIES.map((c) => (
          <button
            key={c.id || "all"}
            onClick={() => onCategoryChange(c.id)}
            data-testid={`filter-cat-${c.id || "all"}`}
            className={`px-4 py-2 text-xs font-grotesk uppercase tracking-[0.18em] transition-colors ${
              category === c.id
                ? "bg-[#C5A059] text-black"
                : "border border-white/10 text-white/70 hover:border-[#C5A059]/40 hover:text-white"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Products table */}
      <div className="bg-[#0a0a0a] border border-white/5 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/5 text-left">
              <th className="p-4 overline">Produit</th>
              <th className="p-4 overline">Catégorie</th>
              <th className="p-4 overline">Marque</th>
              <th className="p-4 overline">Prix</th>
              <th className="p-4 overline">Stock</th>
              <th className="p-4 overline text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-12 text-center text-white/50">Chargement...</td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={6} className="p-12 text-center text-white/50">Aucun produit.</td></tr>
            ) : (
              products.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`product-row-${p.slug}`}
                  className={`border-b border-white/5 last:border-0 hover:bg-white/[0.02] ${!p.is_published ? "opacity-50" : ""}`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#050505] flex-shrink-0 overflow-hidden">
                        {p.images?.[0] && (
                          <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium leading-tight">{p.name}</p>
                        <p className="text-xs text-white/40 mt-0.5">/{p.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-white/70">{labelOfCat(p.category)}</td>
                  <td className="p-4 text-sm text-white/70">{p.brand || "—"}</td>
                  <td className="p-4 text-sm text-[#C5A059] font-grotesk">
                    {p.on_request ? "Sur demande" : `${p.price.toLocaleString("fr-FR")} ${p.currency || "$"}`}
                  </td>
                  <td className="p-4 text-sm text-white/70">{p.stock}</td>
                  <td className="p-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => togglePublish(p)}
                        data-testid={`toggle-publish-${p.slug}`}
                        title={p.is_published ? "Dépublier" : "Publier"}
                        className="p-2 text-white/50 hover:text-[#C5A059] transition-colors"
                      >
                        {p.is_published ? <Eye size={15} /> : <EyeOff size={15} />}
                      </button>
                      <Link
                        to={`/admin/products/${p.id}/edit`}
                        data-testid={`edit-${p.slug}`}
                        className="p-2 text-white/50 hover:text-[#C5A059] transition-colors"
                      >
                        <Edit2 size={15} />
                      </Link>
                      {confirmingDelete === p.id ? (
                        <button
                          onClick={() => onDelete(p.id)}
                          data-testid={`confirm-delete-${p.slug}`}
                          className="px-3 py-1.5 bg-red-500/20 text-red-400 text-xs font-grotesk uppercase tracking-[0.16em] hover:bg-red-500/30"
                        >
                          Confirmer
                        </button>
                      ) : (
                        <button
                          onClick={() => setConfirmingDelete(p.id)}
                          data-testid={`delete-${p.slug}`}
                          className="p-2 text-white/50 hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-white/40 mt-4 font-grotesk">
        Total : <span data-testid="products-total">{products.length}</span> produit{products.length !== 1 ? "s" : ""}
      </p>
    </div>
  );
};

const labelOfCat = (id) => ({
  sneakers: "Sneakers",
  wigs: "Suzel Luxury Hair",
  couture: "Habillement",
  epicerie: "Travel & Market",
  patisserie: "Suzel Bakery",
  printing: "Printing & IT",
}[id] || id);

export default AdminProducts;
